import type { KatalogAuszug, KatalogEintrag, KatalogKnoten, MangelVorlage } from './typen';

/**
 * Vorlage eines Standardmangels: was ein gewählter Mangel in den Eintrag
 * schreibt (Titel, Beschreibung, gekürzte Vorschrift). Quelle ist der
 * Kernauszug bzw. der Gesamtkatalog; Felder am Knoten (Review oder
 * Firmen-Overlay) überschreiben ihn. Eigene Mängel der Firma tragen ihre Texte
 * inline. Die Vorlage trägt nie eine Katalognummer.
 */

/** Nicht leerer Text (nach trim) oder undefined. */
const gesetzt = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v : undefined);

/** Das „*“ vor Titeln der Quelle entfernt schon das Einlesen; hier nur zur Sicherheit. */
const ohneStern = (titel: string): string => titel.replace(/^\s*\*+\s*/, '');

/**
 * Enthält der Text eine Lücke, die der Nutzer füllen muss („Typ: ……“,
 * „Überschrift konkretisieren“, „[Bauteil]“)? Dann zeigt der Schritt
 * „Details“ den gelben Hinweis „Angabe ergänzen“.
 */
export function hatPlatzhalter(text: string): boolean {
    if (typeof text !== 'string' || !text) return false;
    // „……“ oder mehrere „…“ im Text.
    if ((text.match(/…/g) ?? []).length >= 2) return true;
    // Punkt- oder Unterstrich-Lücken („....“, „___“).
    if (/\.{4,}|_{3,}/.test(text)) return true;
    // „Typ: …“, „Hersteller: ...“ — Doppelpunkt mit Lücke dahinter.
    if (/:\s*(?:…|\.{2,}|_{2,})/.test(text)) return true;
    if (/konkretisier/i.test(text)) return true;
    if (/erg(?:ä|ae)nzen/i.test(text)) return true;
    if (text.includes('[')) return true;
    return false;
}

/** Gekürzte Vorschrift: im Kernauszug `vorschriftKurz`, im Gesamtkatalog schon `vorschrift`. */
const vorschriftVon = (a: KatalogAuszug | KatalogEintrag): string =>
    ('vorschriftKurz' in a ? a.vorschriftKurz : a.vorschrift) ?? '';

/**
 * Vorlage aus einem Eintrag (Kernauszug oder Gesamtkatalog). Felder des
 * Knotens (titel/text/vorschrift/dringlichkeit/fristTage) überschreiben den
 * Eintrag. Die Vorschrift ist in beiden Quellen schon gekürzt.
 */
export function vorlageAusAuszug(a: KatalogAuszug | KatalogEintrag, knoten?: KatalogKnoten): MangelVorlage {
    const titel = gesetzt(knoten?.titel) ?? ohneStern(a.titel ?? '');
    const text = gesetzt(knoten?.text) ?? (a.text ?? '');
    const vorschrift = gesetzt(knoten?.vorschrift) ?? vorschriftVon(a);
    const v: MangelVorlage = {
        eintragId: a.id,
        titel,
        text,
        vorschrift,
        platzhalter: hatPlatzhalter(titel) || hatPlatzhalter(text),
    };
    if (knoten?.dringlichkeit) v.dringlichkeit = knoten.dringlichkeit;
    if (typeof knoten?.fristTage === 'number') v.fristTage = knoten.fristTage;
    return v;
}

/**
 * Vorlage aus den Inline-Texten eines Mangel-Knotens (eigene Einträge der
 * Firma, auch „aus dem Gesamtkatalog übernommen“). Ohne Titel und ohne Text → null.
 */
export function vorlageAusKnoten(k: KatalogKnoten): MangelVorlage | null {
    if (!k || k.typ !== 'mangel') return null;
    const titel = gesetzt(k.titel);
    const text = gesetzt(k.text);
    if (!titel && !text) return null;
    const v: MangelVorlage = {
        titel: titel ?? '',
        text: text ?? '',
        vorschrift: gesetzt(k.vorschrift) ?? '',
        platzhalter: hatPlatzhalter(titel ?? '') || hatPlatzhalter(text ?? ''),
    };
    if (k.dringlichkeit) v.dringlichkeit = k.dringlichkeit;
    if (typeof k.fristTage === 'number') v.fristTage = k.fristTage;
    return v;
}
